import { Box, Button, Text } from '@nimbus-ds/components';
import { TagIcon } from '@nimbus-ds/icons';
import { EmptyMessage, InteractiveList } from '@nimbus-ds/patterns';
import { useInfiniteScroll } from 'commons/hooks';
import { SelectedProductInterface } from 'domains/Catalog/Products/components';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import { SelectProductModalItem } from './SelectProductModalItem';
import { SelectProductModalSkeleton } from './SelectProductModalSkeleton';

interface SelectProductModalListProps {
  isLoading: boolean;
  isRefreshing: boolean;
  isSuccess: boolean;
  searchValue: string;
  products?: SelectedProductInterface[] | null;
  selectedProducts: SelectedProductInterface[];
  excludedProductIds: string[];
  onLoadMore: () => void;
  onSelect: (productId: string, checked: boolean) => void;
  onCreateProduct: () => void;
}

export function SelectProductModalList({
  isLoading,
  isRefreshing,
  isSuccess,
  searchValue,
  products,
  selectedProducts,
  excludedProductIds,
  onLoadMore,
  onSelect,
  onCreateProduct,
}: Readonly<SelectProductModalListProps>): JSX.Element {
  const t = useTranslationCatalog();

  const translations = {
    emptyTitle: t('subscriptions.form.products.empty.title'),
    emptyText: t('subscriptions.form.products.empty.text'),
    ctaText: t('subscriptions.form.products.empty.ctaText'),
    emptySearchText: t('subscriptions.form.products.searchEmpty.title'),
    loadingText: t('common.loading'),
  };

  const isEmpty = isSuccess && products?.length === 0;
  const hasEmptyTitle = !!translations.emptyTitle;

  const loadMoreRef = useInfiniteScroll(() => {
    if (isSuccess && !isLoading && !isRefreshing) {
      onLoadMore();
    }
  });

  const renderProductItem = (product: SelectedProductInterface) => {
    const isExcluded = excludedProductIds.includes(product.id);
    const isSelected = selectedProducts.some((prod) => prod.id === product.id);

    return (
      <SelectProductModalItem
        key={product.id}
        product={product}
        isSelected={isSelected}
        isExcluded={isExcluded}
        onChange={(checked) => onSelect(product.id, checked)}
      />
    );
  };

  if (isLoading) {
    return (
      <Box flex="1" overflow="auto" height="100%">
        <Box
          flex="1"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
        >
          <SelectProductModalSkeleton />
        </Box>
        <Box ref={loadMoreRef} height="1px" />
      </Box>
    );
  }

  if (isEmpty && searchValue !== '') {
    return (
      <Box flex="1" overflow="auto" height="100%">
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>{translations.emptySearchText}</Text>
        </Box>
        <Box ref={loadMoreRef} height="1px" />
      </Box>
    );
  }

  if (isEmpty && searchValue === '' && hasEmptyTitle) {
    return (
      <Box flex="1" overflow="auto" height="100%">
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyMessage
            title={translations.emptyTitle}
            text={translations.emptyText}
            icon={<TagIcon size="large" />}
            actions={
              <Button appearance="primary" onClick={onCreateProduct}>
                {translations.ctaText}
              </Button>
            }
          />
        </Box>
        <Box ref={loadMoreRef} height="1px" />
      </Box>
    );
  }

  if (isEmpty && searchValue === '' && !hasEmptyTitle) {
    return (
      <Box flex="1" overflow="auto" height="100%">
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>{translations.emptyText}</Text>
        </Box>
        <Box ref={loadMoreRef} height="1px" />
      </Box>
    );
  }

  return (
    <Box flex="1" overflow="auto" height="100%">
      <InteractiveList>
        {products &&
          products.length > 0 &&
          products.map((product) => renderProductItem(product))}
      </InteractiveList>
      {isRefreshing && (
        <Box display="flex" justifyContent="center">
          <Text fontSize="caption">{translations.loadingText}</Text>
        </Box>
      )}
      <Box ref={loadMoreRef} height="1px" />
    </Box>
  );
}
